import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type {
  WorkflowConfigPublishResponse,
  WorkflowConfigRead,
  WorkflowConfigValidateResponse,
  WorkflowOverlayJson,
} from '@/types/workflowConfig'

export function workflowConfigQueryKey(workflowId: string) {
  return ['workflows', 'config', workflowId] as const
}

export function fetchWorkflowConfig(workflowId: string): Promise<WorkflowConfigRead> {
  return apiClient.get<WorkflowConfigRead>(`/api/workflows/${workflowId}/config`)
}

export function useWorkflowConfig(
  workflowId: string | undefined,
): UseQueryResult<WorkflowConfigRead, Error> {
  return useQuery<WorkflowConfigRead, Error>({
    queryKey: workflowConfigQueryKey(workflowId ?? ''),
    queryFn: () => fetchWorkflowConfig(workflowId!),
    enabled: !!workflowId,
    staleTime: 30_000,
  })
}

/** What a Configure-lane write is based on (SA-416): the stored overlay
 * draft's stamp (null = no stamped draft) and the config base's version
 * number. The server refuses a write whose basis moved — see `writeConflict`. */
export interface ConfigBasis {
  expectedUpdatedAt: string | null
  baseVersionNumber: number | null
}

/** A null stamp is spelled `expected_absent` on every Configure-lane write —
 * a publish included: the seeder's pack-born row carries a publishable draft
 * with no stamp, so "no stamped draft" is a real basis for a publish too. */
export function configBasisBody(basis: ConfigBasis): Record<string, unknown> {
  return {
    ...(basis.expectedUpdatedAt !== null
      ? { expected_updated_at: basis.expectedUpdatedAt }
      : { expected_absent: true }),
    ...(basis.baseVersionNumber !== null ? { expected_base_version_number: basis.baseVersionNumber } : {}),
  }
}

/** SA-1410: a save, a publish and a pack apply also name the live version the
 * session began on — `expected_version_number`, refused 409
 * `workflow_published_since` when the live version moved since (a publish, a
 * restore, a repin). A save is told too, so the person Reloads while their
 * edits are still unsaved. */
export interface PublishBasis extends ConfigBasis {
  versionNumber: number | null
}

export function publishBasisBody(basis: PublishBasis): Record<string, unknown> {
  return {
    ...configBasisBody(basis),
    ...(basis.versionNumber !== null ? { expected_version_number: basis.versionNumber } : {}),
  }
}

export function useSaveWorkflowConfig(
  workflowId: string,
): UseMutationResult<WorkflowConfigRead, Error, { draft: WorkflowOverlayJson } & PublishBasis> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ draft, ...basis }) =>
      apiClient.put<WorkflowConfigRead>(`/api/workflows/${workflowId}/config`, {
        draft,
        ...publishBasisBody(basis),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(workflowConfigQueryKey(workflowId), data)
    },
  })
}

export function useValidateWorkflowConfig(
  workflowId: string,
): UseMutationResult<WorkflowConfigValidateResponse, Error, WorkflowOverlayJson> {
  return useMutation({
    mutationFn: (overlay: WorkflowOverlayJson) =>
      apiClient.post<WorkflowConfigValidateResponse>(
        `/api/workflows/${workflowId}/config/validate`,
        { overlay },
      ),
  })
}

/** Publishes the STORED overlay draft — save first. The basis makes it THIS
 * session's stored draft, against the base its diff was shown for. */
export function usePublishWorkflowConfig(
  workflowId: string,
): UseMutationResult<WorkflowConfigPublishResponse, Error, PublishBasis | void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (basis) =>
      apiClient.post<WorkflowConfigPublishResponse>(
        `/api/workflows/${workflowId}/config/publish`,
        basis ? publishBasisBody(basis) : {},
      ),
    onSuccess: () => {
      // Library, detail, definition, and config views all change shape.
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })
}
